import { AppType } from "client/Api/Apps/common";
import { NedapOnsConnectionDialog } from "client/Features/Settings/Connections/NedapOns/NedapOnsConnectionDialog";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { deepComparison } from "client/Misc/compareUtils";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import { NedapOnsConnectionSelectOptionsModel, getNedapOnsConnections } from "client/Api/Apps/EditApp/appParameters";
import { NedapOnsConnectionListItemModel } from "client/Api/NedapOnsConfiguration/nedapOnsConfiguration";
import {
	SaveNedapOnsAppModel,
	EditNedapOnsAppViewModel,
	NedapOnsConnectionIdentifierViewModel,
	saveNedapOnsApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveNedapOnsAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("connection")
	.add(
		isNotNullNorUndefined()
	);

export type EditNedapOnsAppProps = {
	initialSettings: EditAppSettings<EditNedapOnsAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditNedapOnsApp = forwardRef(
	(
		props: EditNedapOnsAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const alertManager: AlertManager = useAlerts();
		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const [formData, setFormData] = useState<SaveNedapOnsAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);

		const [optionsModel, setOptionsModel] = useState<NedapOnsConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveNedapOnsAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const connectionOptions: readonly SelectItem<NedapOnsConnectionIdentifierViewModel>[] = useMemo(
			(): readonly SelectItem<NedapOnsConnectionIdentifierViewModel>[] => {
				return optionsModel.options
					.map(
						o => ({
							value: {
								id: o.id
							},
							text: o.name
						})
					);
			},
			[optionsModel.options]
		);

		const loadNedapOnsConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const optionsModel: NedapOnsConnectionSelectOptionsModel = await getNedapOnsConnections(
						signal
					);

					setOptionsModel(optionsModel);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			async (item: NedapOnsConnectionListItemModel): Promise<void> => {
				await loadNedapOnsConnections(globalCancellation);

				formHandler.for("connection")
					.attachToSelect()
					.onChange({ id: item.id });
			},
			[
				formHandler,
				globalCancellation,
				loadNedapOnsConnections
			]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadNedapOnsConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadNedapOnsConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveNedapOnsAppModel>(
			formHandler,
			saveNedapOnsApp,
			AppType.NedapOns
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connection", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem label={localize("_Connection")}>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connection").attachToSelect()}
									options={connectionOptions}
									areEqual={deepComparison}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{optionsModel.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={():void => setIsConnectionDialogOpen(true)}
									kind="secondary"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connection").attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<NedapOnsConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);
EditNedapOnsApp.displayName = "EditNedapOnsApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditNedapOnsAppViewModel>
): SaveNedapOnsAppModel {
	const {
		destinationWindow,
		connection
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		connection
	};
}